import { useState } from "react";
import { useIsFetching, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { DEFAULT_BASIS, PRICE_RUN_KEY, unstagePrice, type Basis, type Market, type PriceRunLine, type PriceRuns } from "./api";
import { invalidateBatch } from "./batch";
import {
  COMMIT_REVIEW_COPY, floorCell, grossCell, lineProjectionBody, removeRefusal, reviewGroups, reviewPlace, rrpCell, type ReviewCell,
} from "./commit_review";
import { configQuery, floorOf, numberValue } from "./config";
import { COPY, grossTone, money, type MarginTarget } from "./format";
import { projectionQuery } from "./projection";
import { movedOff, PROPOSE_COPY } from "./propose";
import { rrpLookup, rrpQuery, type RrpReads } from "./rrp";

type ReviewProps = {
  runs: PriceRuns | undefined; markets: Market[]; basis?: Basis; count: number; result: string | null; pending: boolean; areaName: string;
  onConfirm: () => void; onCancel: () => void; onRemoved: () => void;
};

/**
 * The review body (C1 v1.26): one table per market on view, one row per saved line, with the current and new price, the gross
 * margin at both, the floor and the RRP. Each line's figures come from C2's projection at its saved price, read when the review
 * opens and shared with the New price box; Commit never waits for them, because C2 judges every line again when it commits.
 * While the commit is pending the review stays open and Cancel is disabled: the commit lands either way, and its answer belongs
 * in the review. Rendered on its own in tests, because the dialog's portal does not render server-side.
 * C1 v1.41: each line ends in Remove from the batch, the Detail panel's own action (`unstagePrice`), so a line the review shows
 * can leave the batch without opening its row. A remove drops the reads a save drops (`invalidateBatch`); the review re-renders
 * from the re-read runs, so the line goes, the title's count falls and Commit sends the lines that are left (C1 v1.38). Every
 * Remove and Commit are disabled while a commit or a remove is pending and while the runs are being re-read (review round 1,
 * A1: a removed line stays listed until the re-read lands, and must not be clicked twice); the line being removed reads
 * "Removing…". The answer is a status line naming the line, or the refusal as an alert; a success also clears a commit refusal
 * the bar still showed (`onRemoved`). Each Remove is labelled with its line for a screen reader (round 1, A3).
 */
export function CommitReview({ runs, markets, basis = DEFAULT_BASIS, count, result, pending, areaName, onConfirm, onCancel, onRemoved }: ReviewProps) {
  const client = useQueryClient();
  const [note, setNote] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const groups = reviewGroups(runs, markets);
  const lines = groups.flatMap((group) => group.lines);
  const config = useQuery(configQuery());
  const target: MarginTarget = config.data ? numberValue(config.data.settings.margin_target_pct) : undefined;
  const legend = target == null ? (config.data ? COPY.legendTargetNotSet(config.data.settings.margin_target_pct.not_set ?? null) : COPY.legendTargetReading) : COPY.legendTarget(target);
  const rrpQueries = useQueries({ queries: groups.map((group) => rrpQuery(group.market)) });
  const rrps: RrpReads = Object.fromEntries(groups.map((group, i) => [group.market, { overlay: rrpQueries[i].data ?? null, failed: Boolean(rrpQueries[i].error) }]));
  const projections = useQueries({ queries: lines.map((line) => projectionQuery(lineProjectionBody(line, basis))) });
  const byLine = new Map(lines.map((line, i) => [line.id, projections[i]]));
  const remove = useMutation({
    mutationFn: (line: PriceRunLine) => unstagePrice(line.id),
    onSuccess: (_, line) => { invalidateBatch(client); setNote({ tone: "ok", text: COMMIT_REVIEW_COPY.removed(line) }); onRemoved(); },
    onError: (error) => setNote({ tone: "err", text: removeRefusal(error, areaName) }),
  });
  // The runs list alone (`exact`): a per-run read of a committed run re-fetching in the background gates nothing (round 2, R2-1).
  const rereading = useIsFetching({ queryKey: PRICE_RUN_KEY, exact: true }) > 0;
  const busy = pending || remove.isPending || rereading;
  return (
    <div className="space-y-4">
      {/* C1 v1.38: a refusal's re-read can empty the batch while the review is open; it says so, and Commit is disabled. */}
      {lines.length === 0 && <p className="text-sm text-ks-ink">{COPY.nothingSaved}</p>}
      {groups.map((group) => (
        <section key={group.runId} aria-label={COMMIT_REVIEW_COPY.market(group.market, group.lines.length)} className="space-y-1">
          <h3 className="text-sm font-semibold text-ks-ink">{COMMIT_REVIEW_COPY.market(group.market, group.lines.length)}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                {COMMIT_REVIEW_COPY.columns.map((column, i) => <TableHead key={column} className={i >= 1 && i <= 4 ? "text-right" : undefined}>{column}</TableHead>)}
                <TableHead><span className="sr-only">{COMMIT_REVIEW_COPY.removeColumn}</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.lines.map((line) => {
                const read = byLine.get(line.id);
                const p = read?.data;
                const failed = Boolean(read?.error);
                const removing = remove.isPending && remove.variables?.id === line.id;
                return (
                  <TableRow key={line.id}>
                    <TableCell>
                      <div className="font-medium text-ks-ink">{line.product} {line.variant}</div>
                      <div className="text-xs text-ks-grey-1">{reviewPlace(line)}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-ks-grey-1">{line.old_price == null ? COPY.none : money(line.old_price, line.currency)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{money(line.new_price, line.currency)}</TableCell>
                    <TableCell className="text-right tabular-nums" data-review-proposed={line.id}>
                      {line.proposal ? <div className={movedOff(line) ? "font-semibold text-ks-warn" : "text-ks-grey-1"}>{money(line.proposal.price, line.currency)}</div> : <span className="text-ks-grey-1">{COPY.none}</span>}
                      {movedOff(line) && <div className="text-xs text-ks-warn">{PROPOSE_COPY.reviewMoved}</div>}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${p ? grossTone(p.margins.gross_pct, target) : "text-ks-grey-1"}`}>{grossCell(p, failed).text}</TableCell>
                    <CellWithNote cell={floorCell(line, floorOf(config.data?.floors, line.xero_sku))} />
                    <CellWithNote cell={rrpCell(line, p, failed, rrpLookup(rrps, line.market, line.xero_sku, line.marketplace_id))} />
                    <TableCell className="text-right">
                      <Button type="button" size="sm" variant="ghost" disabled={busy} aria-busy={removing} aria-label={COMMIT_REVIEW_COPY.removeLabel(line)} data-remove-line={line.id} onClick={() => { setNote(null); remove.mutate(line); }}>
                        {removing ? COMMIT_REVIEW_COPY.removing : COMMIT_REVIEW_COPY.remove}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </section>
      ))}
      {lines.length > 0 && <p className="text-xs text-ks-grey-1">{COMMIT_REVIEW_COPY.footnote(basis, legend)}</p>}
      {/* AGENTS.md rule 14: a refusal is an alert; a success stays a status line. */}
      {note && <p role={note.tone === "err" ? "alert" : "status"} className={note.tone === "err" ? "text-sm" : "text-sm text-ks-pass"}>{note.text}</p>}
      {result && <p role="alert" className="text-sm">{result}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" data-nonmutating disabled={pending} onClick={onCancel}>{COMMIT_REVIEW_COPY.cancel}</Button>
        {count > 0 && <Button type="button" disabled={busy} onClick={onConfirm}>{pending ? COMMIT_REVIEW_COPY.committing : COMMIT_REVIEW_COPY.confirm(count)}</Button>}
      </div>
    </div>
  );
}

function CellWithNote({ cell }: { cell: ReviewCell }) {
  return (
    <TableCell className="text-sm">
      <div>{cell.text}</div>
      {cell.note && <div className="text-xs text-ks-grey-1">{cell.note}</div>}
    </TableCell>
  );
}

/** The dialog chrome. The body mounts only while open, so the projections are read when the person opens the review; Escape and
 * the overlay close it like Cancel, and not while a commit is pending. No X: Cancel is the one close control. */
export function CommitReviewDialog({ open, onOpenChange, ...props }: Omit<ReviewProps, "onCancel"> & { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (next || !props.pending) onOpenChange(next); }}>
      <DialogContent showCloseButton={false} className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{COMMIT_REVIEW_COPY.title(props.count)}</DialogTitle>
          <DialogDescription>{COMMIT_REVIEW_COPY.description}</DialogDescription>
        </DialogHeader>
        {open && <CommitReview {...props} onCancel={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}
